import React, { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthPage } from '../../hooks/useAuthPage';
import { hashPasswordClient } from '../../utils/passwordUtils';
import { FlowHeader } from './TinycmsSignupFlow';

const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

export const REQUEST_EMAIL_CODE = gql`
  mutation RequestEmailCode($email: String!, $purpose: String) {
    requestEmailCode(email: $email, purpose: $purpose)
  }
`;

const SIGN_IN_WITH_CODE = gql`
  mutation SignInWithEmailCode($email: String!, $code: String!) {
    signInWithEmailCode(email: $email, code: $code)
  }
`;

/** Seconds before "Send a new code" works again. */
export const RESEND_SECONDS = 30;

/**
 * TinyCMS sign-in ("TinyCMS UX optimization review v3.zip", Signup Journey):
 * one column, the same header as signup, and a way into signup for someone
 * new. Session notices (signed out, session expired) come from the same
 * places the shared Login reads them.
 *
 * Two ways in, side by side: email + password, or "Email me a code" (a
 * 6-digit code by email; no password needed). An account made without a
 * password signs in with codes only.
 */
export function TinycmsLogin() {
  useAuthPage();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuth();
  // Email codes by default; a password is one link away (owner, 2026-10-15).
  const [mode, setMode] = useState<'code' | 'password'>('code');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [hashing, setHashing] = useState(false);

  const redirectAfterAuth = () => {
    const target = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
    navigate(target || '/');
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) redirectAfterAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Messages left by logout(), the 401 handler and ErrorBoundary win; otherwise
  // say something only when a specific page turned the person away.
  useEffect(() => {
    const authError = sessionStorage.getItem('authError');
    if (authError) {
      sessionStorage.removeItem('authError');
      setNotice(
        /logged out|signed out/i.test(authError)
          ? { tone: 'ok', text: 'You’ve been signed out.' }
          : { tone: 'error', text: authError }
      );
      return;
    }
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from !== '/') setNotice({ tone: 'error', text: 'Sign in to see that page.' });
    if (from) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [loginMutation, { loading }] = useMutation<{ login: string }>(LOGIN_MUTATION, {
    onCompleted: (data) => {
      login(data.login);
      redirectAfterAuth();
    },
  });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [requestCode, { loading: requesting }] = useMutation(REQUEST_EMAIL_CODE);
  const [signInWithCode, { loading: verifying }] = useMutation<{ signInWithEmailCode: string }>(
    SIGN_IN_WITH_CODE
  );

  const message = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      setFormError('Enter your email.');
      return;
    }
    setFormError('');
    try {
      await requestCode({ variables: { email, purpose: 'sign_in' } });
      setMode('code');
      setCodeSent(true);
      setCode('');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setFormError(message(err, 'The code couldn’t be sent. Try again.'));
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) {
      setFormError('Enter the 6-digit code from the email.');
      return;
    }
    setFormError('');
    try {
      const { data } = await signInWithCode({ variables: { email, code } });
      if (data?.signInWithEmailCode) {
        login(data.signInWithEmailCode);
        redirectAfterAuth();
      }
    } catch (err) {
      setFormError(message(err, 'Sign in failed. Try again.'));
    }
  };

  const switchMode = (next: 'code' | 'password') => {
    setMode(next);
    setCodeSent(false);
    setCode('');
    setFormError('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email) || !password) {
      setFormError('Enter your email and password, or choose Email me a code.');
      return;
    }
    setFormError('');
    setHashing(true);
    try {
      await loginMutation({ variables: { email, password: await hashPasswordClient(password) } });
    } catch (err) {
      setFormError(
        err instanceof Error && err.message ? err.message : 'Sign in failed. Try again.'
      );
    } finally {
      setHashing(false);
    }
  };

  const busy = loading || hashing;
  return (
    <div className="flow">
      {/* No header link: someone signing in isn't shopping, and "Create an
          account" already sits under the form. */}
      <FlowHeader />
      <main className="flow-main flow-main-single">
        <form
          className="flow-signin"
          onSubmit={mode === 'password' ? submit : codeSent ? submitCode : sendCode}
          noValidate
        >
          {notice ? (
            <div className="flow-notice" data-tone={notice.tone}>
              {notice.tone === 'ok' ? <span className="flow-status-dot" /> : null}
              {notice.text}
            </div>
          ) : null}
          <h1 className="flow-h1">Sign in</h1>
          <p className="flow-lede">
            {mode === 'code' && codeSent
              ? `If there’s a TinyCMS account for ${email}, a 6-digit code is on its way. It works for 10 minutes.`
              : 'Pick up where your agents left off.'}
          </p>
          <div className="flow-fields">
            {mode === 'code' && codeSent ? (
              <label className="flow-field">
                <span className="flow-label">Code</span>
                <input
                  className="flow-input flow-code"
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123 456"
                  maxLength={7}
                  autoFocus
                  onChange={(e) => {
                    setCode(e.target.value.replace(/[^\d ]/g, ''));
                    setFormError('');
                  }}
                />
              </label>
            ) : (
              <label className="flow-field">
                <span className="flow-label">Email</span>
                <input
                  className="flow-input"
                  type="email"
                  value={email}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFormError('');
                  }}
                />
              </label>
            )}
            {mode === 'password' ? (
              <label className="flow-field">
                <span className="flow-label flow-label-row">
                  Password
                  <button
                    type="button"
                    className="flow-link"
                    onClick={() => {
                      // Forgot it: straight to a code, sent now if the email is in.
                      switchMode('code');
                      if (/\S+@\S+\.\S+/.test(email)) void sendCode();
                    }}
                  >
                    Forgot it? Use a code
                  </button>
                </span>
                <input
                  className="flow-input"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFormError('');
                  }}
                />
              </label>
            ) : null}
          </div>
          {formError ? <p className="flow-error flow-submit-error">{formError}</p> : null}
          {mode === 'password' ? (
            <>
              <button type="submit" className="flow-btn" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <p className="flow-fine flow-center">
                <button type="button" className="flow-link flow-link-quiet" onClick={() => switchMode('code')}>
                  Email me a code instead
                </button>
              </p>
            </>
          ) : codeSent ? (
            <>
              <button type="submit" className="flow-btn" disabled={verifying}>
                {verifying ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="flow-code-actions">
                <button
                  type="button"
                  className="flow-link"
                  disabled={cooldown > 0 || requesting}
                  onClick={() => void sendCode()}
                >
                  {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
                </button>
                <button type="button" className="flow-link" onClick={() => switchMode('code')}>
                  Use a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <button type="submit" className="flow-btn" disabled={requesting}>
                {requesting ? 'Sending…' : 'Email me a code'}
              </button>
              <p className="flow-fine flow-center">
                <button type="button" className="flow-link flow-link-quiet" onClick={() => switchMode('password')}>
                  Use a password instead
                </button>
              </p>
            </>
          )}
          <div className="flow-signin-foot">
            New to TinyCMS?{' '}
            <button type="button" className="flow-link" onClick={() => navigate('/signup')}>
              Create an account
            </button>
            .
          </div>
        </form>
      </main>
    </div>
  );
}
